/**
 * 生图工作台设计令牌（2026-10-08）。
 *
 * 🔴 为什么要抽这一层：改版前 `studio-workbench.tsx` 1112 行里散落着 **60+ 处**裸
 * Tailwind 字面量（`h-7 rounded-md border border-[var(--border-subtle)] bg-white …`），
 * 同一个「次级按钮」在文件里写了 5 遍、`--text-muted` 标签写了 12 遍。
 * 这类重复是**不一致的温床**：改圆角要改 5 处，漏一处视觉就分叉；
 * 更糟的是它让「对比度不合格」这类问题可以静默存在 —— 因为大家看到的都是
 * 「照着邻居写的」，没人会问「这个颜色够亮吗」。
 *
 * 所以先立契约，再改组件：
 *   - 阶梯（字号 / 间距 / 控件高度 / 圆角）集中在此，**改一处即全站生效**；
 *   - 每一档都注明**为什么是这个值**（实测宽度 / WCAG 阈值 / 项目既有惯例），
 *     而不是「感觉差不多」；
 *   - 组件只引用语义名（`STUDIO.field.label`），不再拼字面量。
 *
 * ── 设计令牌取值依据（2026-10-08 实测，非拍脑袋）────────────────────
 *
 * 【字号】项目既有页面（card.tsx / button.tsx）用 text-sm=14 / text-xs=12 / text-[13px]。
 * 工作台右栏 328px、参数面板两列栅格每格约 150px：
 *   · 14px 中文标签在 150px 格内约 10 字/行，「负向提示词」这类 5 字标签仍安全；
 *   · 12px 在同格内约 12 字/行，可作辅助信息；
 *   · 改版前用的 **10px 在 150px 格内不足 13 字且低于 WCAG 最小字号建议**，
 *     且 `--text-muted` 叠加后几乎不可读 → 本次把承载信息的最小字号提到 12px。
 *
 * 【控件高度】右栏垂直空间吃紧（实测内容 1096px vs 可视 964px），必须压高度；
 * 但 < 28px 会让 iOS Safari 自动放大（< 44px 不满足无障碍点击区）。
 * 取 32px 为输入下限、36px 为主控件、40px 为主 CTA。
 *
 * 【对比度】实测 WCAG 比值（on #FFFFFF）：
 *   --text-muted      #A0A098 → **2.63:1  ❌ 不达标**（AA 正文需 4.5:1）
 *   --text-secondary  #6B6B64 → 5.37:1  ✅
 *   --text-primary    #1A1A1A → 17.40:1 ✅
 *   --primary         #E8553A → 3.63:1  ⚠️ 仅达 AA-large（≥18.66px 或 ≥14px 粗体）
 * 结论：`--text-muted` **不得用于任何承载信息的文字**，只允许出现在纯装饰
 * （占位框边框、计数角标背景）。这条写进 `field.hint` 的注释里，避免下一个人又用回 muted。
 */

// ── 间距阶梯 ────────────────────────────────────────────────────
// 4 的倍数，与 Tailwind 默认刻度对齐；命名按「意图」而非「数值」。
export const S = {
  /** 同一元素内的紧密元素（标签↔输入框） */
  tight: "gap-1",
  /** 同组控件之间（数量按钮之间、图标按钮之间） */
  group: "gap-1.5",
  /** 字段之间（提示词↔参数面板↔尺寸区） */
  field: "gap-2.5",
  /** 区块之间（栏内各分区） */
  block: "gap-3",
  /** 栏与栏之间（栅格 gap） */
  column: "gap-4",
} as const;

/** 栏内纵向堆叠：统一成一个类，避免 space-y-2/2.5/3 三档混用 */
export const stackBlock = "flex flex-col gap-3";
export const stackField = "flex flex-col gap-2.5";
export const stackTight = "flex flex-col gap-1.5";

// ── 字号阶梯 ────────────────────────────────────────────────────
export const F = {
  /** 区块标题（面板小标题） */
  sectionTitle: "text-xs font-semibold text-[var(--text-primary)]",
  /** 字段标签：承载信息 → 用 secondary，不用 muted（见文件头对比度实测） */
  label: "text-xs font-medium text-[var(--text-secondary)]",
  /** 正文 / 输入值 */
  value: "text-sm text-[var(--text-primary)]",
  /** 辅助说明 / 元数据：仍需可读，故 secondary 而非 muted */
  meta: "text-xs text-[var(--text-secondary)]",
  /** 纯装饰性计数（不承担信息，配合其它文本出现） */
  deco: "text-[11px] tabular-nums text-[var(--text-muted)]",
  /** 长文本（提示词全文） */
  prose: "text-xs leading-relaxed text-[var(--text-primary)]",
} as const;

// ── 控件高度阶梯 ────────────────────────────────────────────────
// 28px 以下会触发 iOS Safari focus 自动缩放；40px 用于唯一主 CTA。
export const H = {
  /** 迷你控件（数量选择 1-4、翻页箭头） */
  xs: "h-7",
  /** 输入框 / 下拉（表单主体） */
  sm: "h-8",
  /** 按钮（操作区） */
  md: "h-9",
  /** 主 CTA（生成） */
  lg: "h-10",
} as const;

/** 统一圆角：改版前 rounded-md/lg/xl 三档混用，同样输入框有 md、面板有 xl */
export const R = {
  control: "rounded-lg",
  panel: "rounded-xl",
  chip: "rounded-md",
} as const;

// ── 焦点环 ──────────────────────────────────────────────────────
// 🔴 改版前只有缩略图有 focus-visible 环，其余 input/select 全靠 `focus:border-*`
// （改边框色对键盘用户几乎不可见，且不满足 WCAG 2.4.7 焦点可见）。
// 现统一：所有可交互元素用 ring，不改边框 —— 改边框会引发布局抖动。
export const FOCUS =
  "outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/45 focus-visible:ring-offset-1 focus-visible:ring-offset-white";

// ── 控件基座 ────────────────────────────────────────────────────
const controlBase =
  `${H.sm} w-full ${R.control} border border-[var(--border-subtle)] bg-white px-2 ${F.value} ${FOCUS} ` +
  "transition-colors hover:border-[var(--border-hover)] disabled:cursor-not-allowed disabled:bg-[var(--surface)] disabled:text-[var(--text-muted)] disabled:opacity-70";

export const inputCls = `${controlBase} disabled:hover:border-[var(--border-subtle)]`;

/**
 * 🔴 窄宽数字框（2026-10-08 第 5 轮，滑块回显用）。
 *
 * 为什么不能直接 `inputCls + " w-[4.25rem]"`：
 *   `controlBase` 里写死了 `w-full`。Tailwind 的宽度类冲突**不按类名在
 *   class 属性里的先后顺序生效**，而按 CSS 产物里的规则顺序 ——
 *   `w-full` 与 `w-[4.25rem]` 谁赢取决于 Tailwind 生成的先后，
 *   开发时看到的是「我写在后面所以应该生效」，实测却是 `w-full` 赢。
 *
 * 真实翻车（CDP 实测）：
 *   数字框实际宽 **336px**（= 整个 flex 容器宽）→ 把并排的滑块挤成 **0px**，
 *   滑块**完全不可见**。tsc 绿、门禁绿、页面看着「有个数字框」，看不出任何异常。
 *
 * 所以这里给一个**结构上就没有宽度类**的变体，宽度由调用点单独决定 ——
 * 不是靠类名覆盖，而是从源头不存在冲突项。
 */
export const inputFixedCls =
  `${H.sm} ${R.control} border border-[var(--border-subtle)] bg-white px-2 ${F.value} ${FOCUS} ` +
  "transition-colors hover:border-[var(--border-hover)] disabled:cursor-not-allowed " +
  "disabled:bg-[var(--surface)] disabled:text-[var(--text-muted)] disabled:opacity-70 " +
  "disabled:hover:border-[var(--border-subtle)]";

/** 下拉：额外禁掉 iOS 横向溢出与默认内边距 */
export const selectCls = `${controlBase} appearance-none bg-[no-repeat] pr-7 disabled:hover:border-[var(--border-subtle)]`;
/** 文本域：不用固定高度，允许用户纵向拉伸 */
export const textareaCls =
  `w-full resize-y ${R.control} border border-[var(--border-subtle)] bg-white px-2.5 py-2 ${F.value} leading-relaxed ${FOCUS} ` +
  "transition-colors hover:border-[var(--border-hover)] disabled:cursor-not-allowed disabled:bg-[var(--surface)] disabled:opacity-70";

/**
 * 下拉箭头背景（appearance-none 后需要自绘）。
 * 内联 SVG data URI，零依赖零请求 —— 与项目其它页面的下拉保持同款视觉。
 */
export const CARET_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%236B6B64' stroke-width='1.6' stroke-linecap='round'%3E%3Cpath d='M4 6.5L8 10.5L12 6.5'/%3E%3C/svg%3E\")";

// ── 按钮变体 ────────────────────────────────────────────────────
type BtnVariant = "primary" | "outline" | "ghost" | "danger";

const BTN_VARIANT: Record<BtnVariant, string> = {
  primary:
    "bg-[var(--primary)] text-white shadow-sm shadow-[var(--primary)]/25 hover:brightness-105 active:brightness-95",
  outline:
    "border border-[var(--border-subtle)] bg-white text-[var(--text-primary)] hover:border-[var(--primary)] hover:text-[var(--primary)]",
  ghost:
    "text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]",
  danger:
    "border border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:bg-red-100",
};

/** 按钮 className 工厂：变体 × 尺寸。`className` 用于覆盖（如宽度）。 */
export function btn(
  variant: BtnVariant = "outline",
  opts?: { block?: boolean; size?: keyof typeof H; className?: string },
): string {
  const { block, size = "md", className = "" } = opts ?? {};
  return [
    "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap font-medium",
    "transition-all duration-150 active:scale-[0.98]",
    "disabled:pointer-events-none disabled:opacity-40",
    H[size],
    R.control,
    BTN_VARIANT[variant],
    block ? "w-full" : "",
    FOCUS,
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

/** 图标按钮（方形）：翻页、关闭、删除 */
export function iconBtn(opts?: {
  size?: keyof typeof H;
  tone?: "default" | "overlay" | "danger";
  className?: string;
}): string {
  const { size = "xs", tone = "default", className = "" } = opts ?? {};
  const toneCls =
    tone === "overlay"
      ? // 压在图片上：半透明深底，任何底图上都够对比
        "bg-black/55 text-white backdrop-blur-sm hover:bg-black/75"
      : tone === "danger"
        ? "text-[var(--destructive)] hover:bg-red-50"
        : "border border-[var(--border-subtle)] bg-white text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)]";
  return [
    "inline-flex shrink-0 items-center justify-center transition-all duration-150 active:scale-95",
    "disabled:pointer-events-none disabled:opacity-35",
    H[size],
    R.control,
    size === "xs" ? "w-7" : size === "sm" ? "w-8" : "w-9",
    toneCls,
    FOCUS,
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

// ── 面板 ────────────────────────────────────────────────────────
/**
 * 统一面板容器。改版前有三种写法并存：
 *   `rounded-xl border bg-white/60 p-3` / `rounded-lg border bg-white/60 p-2.5` / 裸 div。
 * 「区块」是页面里唯一的结构单位，它必须长得一样，否则用户读不出分组。
 */
export const panelCls = `${R.panel} border border-[var(--border-subtle)] bg-white p-3`;
/** 半透明白（叠在 --surface 上，用于次级面板） */
export const panelSoftCls = `${R.panel} border border-[var(--border-subtle)] bg-white/60 p-2.5`;

// ── 分区标题 ────────────────────────────────────────────────────
/** 区块小标题：左标题 + 右可选计数的通栏行 */
export const sectionHeadCls = "flex items-baseline justify-between gap-2";
